import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import FeatureSection from "@/components/site/FeatureSection";
import BookingCTA from "@/components/home/BookingCTA";
import { Reveal, WordsReveal } from "@/components/site/Reveal";
import { SITE } from "@/data/site";
import { INDOOR_GAMES } from "@/data/content";

const VALUES = [
  { no: "01", t: "Eco-Conscious", d: "Built with local timber and stone, powered thoughtfully, treading lightly on the land we love." },
  { no: "02", t: "Malnad Hospitality", d: "Warmth that feels like family — every guest is welcomed the way the hills have welcomed travellers for generations." },
  { no: "03", t: "Rooted in Nature", d: "No manicured lawns pretending to be a resort. Just the real, breathing forest of the Western Ghats." },
];

export default function About() {
  return (
    <PageShell testId="about-page">
      <PageHero
        overline="About The Resort"
        title={["Our Story", "In The Mist"]}
        subtitle="A family's love for the Western Ghats, shaped into a quiet retreat."
        image={SITE.aboutImage1}
      />

      <section className="bg-ivory py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-5 md:px-10">
          <h2 className="font-display text-3xl font-bold leading-[1.15] tracking-tight text-forest sm:text-4xl md:text-5xl">
            <WordsReveal text="Vruksha Valley began with a simple wish — to share the forest we grew up in, without changing what makes it magic." />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 text-lg text-charcoal/70">
              Nestled near Kalasa in Chikkamagaluru, our resort is a cluster of hand-crafted A-frame cottages gathered around an infinity pool and a bonfire clearing. Mornings arrive with mist rolling over the ridgelines and the call of forest birds; evenings settle into filter coffee, home-cooked Malnad meals and long conversations by the fire.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 text-lg text-charcoal/70">
              We believe luxury is not marble and chandeliers — it is silence, clean air, real food and time that finally slows down. That is what we offer, wrapped in the timeless hospitality of the Malnad hills.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest py-24 text-ivory md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><p className="mb-14 text-xs uppercase tracking-widest-2 text-gold md:text-sm">What We Stand For</p></Reveal>
          <div className="grid gap-10 md:grid-cols-3 md:gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.no} delay={i * 0.12} className="border-t border-ivory/20 pt-6">
                <p className="font-serif text-5xl text-gold/70">{v.no}</p>
                <h3 className="mt-4 font-serif text-2xl">{v.t}</h3>
                <p className="mt-3 text-ivory/70">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FeatureSection
        testId="about-feature"
        overline="The Setting"
        title="Where the forest writes the rhythm"
        intro="Set inside a living coffee estate, every cottage frames a different piece of the valley. Wake to layered ridgelines, wander to the infinity pool by day, and end under a sky thick with stars."
        images={[SITE.aboutImage2, SITE.aboutImage1]}
        cta={{ label: "Plan your stay", to: "/contact" }}
      />

      <FeatureSection
        testId="about-indoor-games"
        overline="Rainy-Day Fun"
        title={INDOOR_GAMES.title}
        intro={INDOOR_GAMES.intro}
        images={[SITE.poolImage, SITE.aboutImage2]}
        reverse
        dark
        list={["Carrom", "Chess & board games", "Card games", "Cosy indoor lounge"]}
      />

      <BookingCTA />
    </PageShell>
  );
}
